import React, { useState } from 'react';
import {
	Paper,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableRow,
	TextField,
	Tooltip,
	Box,
} from 'components/mui';
import { Modal, Typography } from '@mui/material';
import styles from './CheckpointEditor.module.css';
import DropdownComponent from './DropdownComponent';
import { useNotificationProvider } from 'hooks';

interface CheckpointStatusResponseProps {
	status: CheckpointStatus[];
	onUpdateStatus: (updatedStatus: CheckpointStatus[]) => void;
	dropdowns: CheckpointDropdown[];
	onUpdateDropdown: (updatedValues: CheckpointDropdown[]) => void;
}

const CheckpointStatusResponse = (props: CheckpointStatusResponseProps) => {
	const { status, onUpdateStatus, dropdowns, onUpdateDropdown } = props;

	const [openModal, setOpenModal] = React.useState<boolean>(false);

	const [dropdownType, setDropdownType] =
		useState<CheckpointDropdownTypes | null>(null);

	const { notifyError } = useNotificationProvider();

	const autoFillCounts = {
		0: dropdowns.filter(
			(item) => item.DropdownType === 'clearStatusAutoFill',
		).length,
		1: dropdowns.filter(
			(item) => item.DropdownType === 'warningStatusAutoFill',
		).length,
		2: dropdowns.filter(
			(item) => item.DropdownType === 'emergencyStatusAutoFill',
		).length,
	};

	const handleAutoFillClick = (index: number) => {
		setOpenModal(true);
		if (index === 0) {
			setDropdownType('clearStatusAutoFill');
		} else if (index === 1) {
			setDropdownType('warningStatusAutoFill');
		} else if (index === 2) {
			setDropdownType('emergencyStatusAutoFill');
		}
	};
	const handleCloseModal = () => {
		const errorIDs = dropdowns.filter(
			(item) => item.DropdownType === dropdownType && item.isError,
		);
		if (errorIDs.length > 0) {
			notifyError(
				'Please fill in all the fields before closing the modal.',
			);
			return;
		} else {
			setOpenModal(false);
		}
	};

	return (
		<Paper className={styles.table}>
			<Table size="small">
				<TableBody>
					<TableRow>
						<TableCell key="header" />

						{status.map((_status, index) => (
							<TableCell key={`name-${index}`}>
								{index === 0 ? 'Green' : ''}
								{index === 1 ? 'Amber' : ''}
								{index === 2 ? 'Red' : ''}
								<Tooltip
									title={`Toggle to ${
										_status.isEnabled ? 'disable' : 'enable'
									} ${
										index === 0
											? 'green'
											: index === 1
											? 'amber'
											: 'red'
									} status`}
								>
									<Switch
										checked={_status.isEnabled}
										color="primary"
										size="medium"
										onChange={(e) => {
											const newStatus = [...status];
											newStatus[index].isEnabled =
												e.target.checked;
											onUpdateStatus(newStatus);
										}}
									/>
								</Tooltip>
							</TableCell>
						))}
					</TableRow>
					<TableRow>
						<TableCell key="name-header">Name</TableCell>
						{status?.map((_status, index) => (
							<TableCell key={`name-${index}`}>
								<TextField
									disabled={!_status.isEnabled}
									size="small"
									variant="outlined"
									value={_status.Name}
									error={_status.isError}
									helperText={_status.isError && 'Required.'}
									onChange={(e) => {
										const newStatus = [...status];
										const name = e.target.value;
										newStatus[index].Name = name;
										newStatus[index].isError = name === '';
										onUpdateStatus(newStatus);
									}}
								/>
							</TableCell>
						))}
					</TableRow>
					<TableRow>
						<TableCell key="name-header">Require Comment</TableCell>
						{status.map((_status, index) => {
							const showAutoFill =
								_status.isEnabled && _status.isCommentRequired;
							return (
								<TableCell key={`name-${index}`}>
									<Box
										display={'flex'}
										flexDirection={'row'}
										alignItems={'center'}
										gap={0.5}
									>
										<Switch
											disabled={!_status.isEnabled}
											checked={_status.isCommentRequired}
											color="primary"
											size="medium"
											onChange={(e) => {
												const newStatus = [...status];
												newStatus[
													index
												].isCommentRequired =
													e.target.checked;
												onUpdateStatus(newStatus);
											}}
										/>
										{showAutoFill && (
											<Typography
												variant="caption"
												sx={{
													color: 'primary.main',
													cursor: 'pointer',
													'&:hover': {
														color: 'primary.dark',
													},
												}}
												onClick={() =>
													handleAutoFillClick(index)
												}
											>
												{`${
													autoFillCounts[
														index as keyof typeof autoFillCounts
													]
												} autofill options `}
											</Typography>
										)}
									</Box>
								</TableCell>
							);
						})}
					</TableRow>
				</TableBody>
			</Table>
			<Modal open={openModal} onClose={handleCloseModal}>
				<Box className={styles.modalBox}>
					<DropdownComponent
						dropdowns={dropdowns}
						onUpdateDropdown={onUpdateDropdown}
						dropdownType={dropdownType}
						headerText={` ${
							dropdownType === 'clearStatusAutoFill'
								? 'Green'
								: dropdownType === 'warningStatusAutoFill'
								? 'Amber'
								: 'Red'
						} Status Auto-fill Options`}
					/>
				</Box>
			</Modal>
		</Paper>
	);
};
export default CheckpointStatusResponse;
